import { useMemo } from "react";

import { type TableSpec, type ViewColumnDef, type ViewSpec } from "@moment/api-collab/api-types";
import { type SqliteColumnDef } from "@moment/api-collab/differential/schema/index";

export interface HydratedColumnDef {
	columnDef: SqliteColumnDef;
	displayOptions?: ViewColumnDef;
}

export interface HydratedViewColumnDefs {
	primaryDisplayColumn: HydratedColumnDef | undefined;
	topRightColumns: [number, HydratedColumnDef][];
	bottomRightColumns: [number, HydratedColumnDef][];
	bottomLeftColumns: [number, HydratedColumnDef][];
	unplacedColumns: [number, HydratedColumnDef][];
	allColumns: HydratedColumnDef[];
}

export const useHydratedColumnDefs = (
	tableSpec: TableSpec | undefined,
	viewSpec: ViewSpec | undefined
): HydratedViewColumnDefs => {
	return useMemo(() => {
		const columnDisplayOptsByName = new Map(
			(viewSpec?.columns ?? []).map((displayOption) => [
				displayOption.linkedColumn,
				displayOption,
			])
		);
		const allColumnsWithDisplayOptions = (tableSpec?.columns ?? []).flatMap((columnDef) =>
			columnDef.primaryKey
				? []
				: [{ columnDef, displayOptions: columnDisplayOptsByName.get(columnDef.name) }]
		);

		// Find explicit primaryDisplayColumn first, then process other columns
		const explicitPrimaryDisplayColumn = allColumnsWithDisplayOptions.find(
			(d) => d.displayOptions?.primaryDisplayColumn
		);

		let firstTextColumn: HydratedColumnDef | undefined;
		const topRightColumns: [number, HydratedColumnDef][] = [];
		const bottomRightColumns: [number, HydratedColumnDef][] = [];
		const bottomLeftColumns: [number, HydratedColumnDef][] = [];
		const unplacedColumns: [number, HydratedColumnDef][] = [];

		for (const [index, d] of allColumnsWithDisplayOptions.entries()) {
			// Skip the column that's set as explicit primary display column
			if (d === explicitPrimaryDisplayColumn) {
				continue;
			}

			// Skip columns that are explicitly hidden on cards
			if (d.displayOptions?.visibleOnCard === false) {
				continue;
			}

			// Only set firstTextColumn as fallback if there's no explicit primaryDisplayColumn
			if (
				d.displayOptions?.placementHint === undefined &&
				d.columnDef.type === "TEXT" &&
				!firstTextColumn &&
				!explicitPrimaryDisplayColumn
			) {
				firstTextColumn = d;
				continue;
			}

			if (d.displayOptions?.placementHint === "top-right" && topRightColumns.length < 2) {
				topRightColumns.push([index, d]);
			} else if (
				d.displayOptions?.placementHint === "bottom-right" &&
				bottomRightColumns.length < 2
			) {
				bottomRightColumns.push([index, d]);
			} else if (
				d.displayOptions?.placementHint === "bottom-left" &&
				bottomLeftColumns.length < 2
			) {
				bottomLeftColumns.push([index, d]);
			} else {
				unplacedColumns.push([index, d]);
			}
		}

		return {
			primaryDisplayColumn: explicitPrimaryDisplayColumn ?? firstTextColumn,
			topRightColumns,
			bottomRightColumns,
			bottomLeftColumns,
			unplacedColumns,
			allColumns: allColumnsWithDisplayOptions,
		};
	}, [tableSpec?.columns, viewSpec?.columns]);
};
